import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";

const ViewProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [projectData, setProjectData] = useState(null);
  const [editableData, setEditableData] = useState(null);
  const [expandedSupplier, setExpandedSupplier] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const response = await axios.post(
          `${baseURL}get_project_detail`,
          { projectId }
        );
        const initialData = {
          projectCreate: response.data.projectCreate || {},
          addSupplier: response.data.addSupplier?.map((supplier) => ({
            ...supplier,
            details: {
              ...supplier.details,
              moduleWattageData: supplier.details?.moduleWattageData.map((wattage) => ({
                ...wattage,
                i1Location: wattage.i1Location || "",
                i2Location: wattage.i2Location || "",
                i3Location: wattage.i3Location || "",
              })) || [
                {
                  moduleWattageWp: "",
                  moduleWattageNos: "",
                  binningOption: "",
                  i1CurrentBinning: "",
                  i2CurrentBinning: "",
                  i3CurrentBinning: "",
                  i1Location: "",
                  i2Location: "",
                  i3Location: "",
                },
              ],
              materials: supplier.details?.materials || [
                { name: "", make: "", origin: "", description: "" },
              ],
            },
          })) || [],
        };
        setProjectData(initialData);
        setEditableData(initialData);
      } catch (error) {
        console.error("Error fetching project data:", error);
      }
    };
    fetchProjectData();
  }, [projectId]);

  const handleChange = (section, field, value, supplierIdx = null, wattageIdx = null, matIdx = null) => {
    setEditableData((prevData) => {
      const updatedData = JSON.parse(JSON.stringify(prevData));
      if (section === "projectCreate") {
        updatedData.projectCreate[field] = value;
      } else if (section === "addSupplier" && supplierIdx !== null) {
        if (!updatedData.addSupplier[supplierIdx].details) {
          updatedData.addSupplier[supplierIdx].details = {};
        }
        if (wattageIdx !== null && updatedData.addSupplier[supplierIdx].details.moduleWattageData) {
          updatedData.addSupplier[supplierIdx].details.moduleWattageData[wattageIdx][field] = value;
        } else if (matIdx !== null && updatedData.addSupplier[supplierIdx].details.materials) {
          updatedData.addSupplier[supplierIdx].details.materials[matIdx][field] = value;
        } else {
          updatedData.addSupplier[supplierIdx].details[field] = value;
        }
      }
      return updatedData;
    });
  };

  const handleSave = async () => {
    try {
      await axios.put(`${baseURL}edit_project`, editableData);
      alert("Project data updated successfully!");
    } catch (error) {
      console.error("Error saving project data:", error);
    }
  };

  const toggleSupplier = (index) => {
    setExpandedSupplier(expandedSupplier === index ? null : index);
  };

  if (!projectData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-lg font-semibold text-gray-600 animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors mb-4"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div className="bg-white rounded-xl shadow-md p-6 mb-6 transition-all hover:shadow-lg">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Project Details</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-gray-700 font-medium">Project Name</label>
              <p className="text-gray-600 mt-1">{projectData.projectCreate.projectName}</p>
            </div>
            <div>
              <label className="text-gray-700 font-medium">SPV Name</label>
              <input
                type="text"
                value={editableData.projectCreate.spvName}
                onChange={(e) => handleChange("projectCreate", "spvName", e.target.value)}
                className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500 focus:outline-none"
                placeholder="Enter SPV Name"
              />
            </div>
            <div>
              <label className="text-gray-700 font-medium">Project ID</label>
              <p className="text-gray-600 mt-1">{projectData.projectCreate.projectId}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-700">Suppliers</h2>
          {editableData.addSupplier.map((supplier, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md overflow-hidden transition-all"
            >
              <button
                onClick={() => toggleSupplier(index)}
                className="w-full p-4 flex justify-between items-center bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <h3 className="text-lg font-semibold text-gray-800">Supplier: {supplier.name}</h3>
                <svg
                  className={`w-5 h-5 transform transition-transform ${expandedSupplier === index ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {expandedSupplier === index && (
                <div className="p-6 animate-fade-in">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-gray-700 font-medium">Shift Type</label>
                      <input
                        type="text"
                        value={supplier.shift}
                        onChange={(e) => handleChange("addSupplier", "shift", e.target.value, index)}
                        className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                        placeholder="Enter Shift Type"
                      />
                    </div>
                  </div>

                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Module Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {[
                      { label: "Module Type", field: "moduleType" },
                      { label: "Cell Size", field: "cellSize" },
                      { label: "Cell Type", field: "cellType" },
                      { label: "Number of Cells", field: "numberOfCells", type: "number" },
                      { label: "Module Size", field: "moduleSize" },
                      { label: "Mounting Holes", field: "mountingHoles" },
                      { label: "Module Technology", field: "moduleTechnology" },
                    ].map(({ label, field, type = "text" }) => (
                      <div key={field}>
                        <label className="text-gray-700 font-medium">{label}</label>
                        <input
                          type={type}
                          value={supplier.details[field] || ""}
                          onChange={(e) => handleChange("addSupplier", field, e.target.value, index)}
                          className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                          placeholder={`Enter ${label}`}
                        />
                      </div>
                    ))}
                  </div>

                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Module Wattage Data</h4>
                  {supplier.details.moduleWattageData.map((wattage, wattageIdx) => (
                    <div key={wattageIdx} className="bg-gray-50 p-4 rounded-lg mb-4 shadow-sm">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-gray-700 font-medium">Module Wattage (Wp)</label>
                          <input
                            type="number"
                            value={wattage.moduleWattageWp}
                            onChange={(e) =>
                              handleChange("addSupplier", "moduleWattageWp", e.target.value, index, wattageIdx)
                            }
                            className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                          />
                        </div>
                        <div>
                          <label className="text-gray-700 font-medium">Number of Modules</label>
                          <input
                            type="number"
                            value={wattage.moduleWattageNos}
                            onChange={(e) =>
                              handleChange("addSupplier", "moduleWattageNos", e.target.value, index, wattageIdx)
                            }
                            className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                          />
                        </div>
                        <div>
                          <label className="text-gray-700 font-medium">Binning Option</label>
                          <input
                            type="text"
                            value={wattage.binningOption}
                            onChange={(e) =>
                              handleChange("addSupplier", "binningOption", e.target.value, index, wattageIdx)
                            }
                            className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                          />
                        </div>
                        <div>
                          <label className="text-gray-700 font-medium">I1 Current Binning</label>
                          <input
                            type="text"
                            value={wattage.i1CurrentBinning}
                            onChange={(e) =>
                              handleChange("addSupplier", "i1CurrentBinning", e.target.value, index, wattageIdx)
                            }
                            className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                            placeholder="Enter I1 Current Binning"
                          />
                        </div>
                        <div>
                          <label className="text-gray-700 font-medium">I2 Current Binning</label>
                          <input
                            type="text"
                            value={wattage.i2CurrentBinning}
                            onChange={(e) =>
                              handleChange("addSupplier", "i2CurrentBinning", e.target.value, index, wattageIdx)
                            }
                            className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                            placeholder="Enter I2 Current Binning"
                          />
                        </div>
                        <div>
                          <label className="text-gray-700 font-medium">I3 Current Binning</label>
                          <input
                            type="text"
                            value={wattage.i3CurrentBinning}
                            onChange={(e) =>
                              handleChange("addSupplier", "i3CurrentBinning", e.target.value, index, wattageIdx)
                            }
                            className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                            placeholder="Enter I3 Current Binning"
                          />
                        </div>
                        {Object.entries(wattage)
                          .filter(([key]) => key.startsWith("i") && key.endsWith("Location"))
                          .map(([key, value]) => (
                            <div key={key}>
                              <label className="text-gray-700 font-medium">
                                {key.replace(/([i]\d)(Location)/, "$1 Storage $2")}
                              </label>
                              <input
                                type="text"
                                value={value}
                                onChange={(e) =>
                                  handleChange("addSupplier", key, e.target.value, index, wattageIdx)
                                }
                                className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                                placeholder={`Enter ${key.replace(/([i]\d)(Location)/, "$1 $2")}`}
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}

                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Materials</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {supplier.details.materials.map((material, matIdx) => (
                      <div key={matIdx} className="bg-gray-50 p-4 rounded-lg shadow-sm">
                        <div className="space-y-3">
                          <div>
                            <label className="text-gray-700 font-medium">Name</label>
                            <input
                              type="text"
                              value={material.name}
                              onChange={(e) =>
                                handleChange("addSupplier", "name", e.target.value, index, null, matIdx)
                              }
                              className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                              placeholder="Material Name"
                            />
                          </div>
                          <div>
                            <label className="text-gray-700 font-medium">Make</label>
                            <input
                              type="text"
                              value={material.make}
                              onChange={(e) =>
                                handleChange("addSupplier", "make", e.target.value, index, null, matIdx)
                              }
                              className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                              placeholder="Material Make"
                            />
                          </div>
                          <div>
                            <label className="text-gray-700 font-medium">Origin</label>
                            <input
                              type="text"
                              value={material.origin}
                              onChange={(e) =>
                                handleChange("addSupplier", "origin", e.target.value, index, null, matIdx)
                              }
                              className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                              placeholder="Material Origin"
                            />
                          </div>
                          <div>
                            <label className="text-gray-700 font-medium">Description</label>
                            <textarea
                              value={material.description}
                              onChange={(e) =>
                                handleChange("addSupplier", "description", e.target.value, index, null, matIdx)
                              }
                              className="mt-1 w-full p-2 border rounded-md focus:ring-2 focus:ring-gray-500"
                              placeholder="Material Description"
                              rows="2"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-[#424752] text-white rounded-md hover:bg-gray-600 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
              />
            </svg>
            Save Changes
          </button>
        </div>
      </div>

      <style>{`
        .animate-fade-in {
          animation: fadeIn 0.3s ease-in;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default ViewProjectDetails;